/**
 * Domain constants shared by the Worker and the browser.
 *
 * Anything the front end needs to render a picker and the Worker needs to
 * validate lives here, so the two can never drift apart.
 */

import { SUPPORTED_LANGUAGES, type SupportedLanguage } from './languages.ts';
import type { LocalizedText } from './localized-text.ts';

/** 10,000 basis points = 1.00 (spec §9.4). */
export const BASIS_POINTS_SCALE = 10_000;

/**
 * The largest amount any money field accepts, in cents — a million major
 * units. Enforced by `centsSchema` and `nonNegativeCentsSchema`, and shared
 * here so that anything *producing* an amount for one of those fields can
 * decline before the field does rather than after.
 */
export const MAX_AMOUNT_CENTS = 100_000_000;

/**
 * How many copies of one room a quote line may represent.
 *
 * Raised from 100 so a floor of offices or a bank of restrooms fits on one
 * line. A whole building of apartments is a group quantity, not a room one.
 */
export const MAX_ROOM_QUANTITY = 1_000;

/** The most rooms one quote can hold (`quoteChangesSchema`). */
export const MAX_QUOTE_ROOMS = 200;

/** The most rooms one layout can hold (`createGroupTemplateSchema`). */
export const MAX_LAYOUT_ROOMS = 100;

/** The longest note for the customer a quote can carry. */
export const MAX_QUOTE_CUSTOMER_NOTES_LENGTH = 8_000;

/**
 * How many copies of one group a quote may price — forty apartments, a
 * hundred hotel rooms. Ten thousand is past any property this product is
 * asked to bid, and still a number the minute math can hold.
 */
export const MAX_GROUP_QUANTITY = 10_000;

// ---------------------------------------------------------------------------
// Room size and condition multipliers (spec §9.4)
// ---------------------------------------------------------------------------

export interface MultiplierOption {
  readonly code: string;
  readonly label: string;
  readonly basisPoints: number;
  readonly description: string;
}

export const ROOM_SIZES = [
  { code: 'small', label: 'Small', basisPoints: 7_500, description: 'Compact — powder room, closet, small bedroom' },
  { code: 'medium', label: 'Medium', basisPoints: 10_000, description: 'Typical size for this room type' },
  { code: 'large', label: 'Large', basisPoints: 12_500, description: 'Noticeably larger than typical' },
  {
    code: 'extra_large',
    label: 'Extra large',
    basisPoints: 15_000,
    description: 'Great room, primary suite, open plan',
  },
] as const satisfies readonly MultiplierOption[];

export const ROOM_CONDITIONS = [
  { code: 'light', label: 'Light', basisPoints: 8_500, description: 'Already tidy, regular upkeep' },
  { code: 'average', label: 'Average', basisPoints: 10_000, description: 'Normal household soil' },
  { code: 'heavy', label: 'Heavy', basisPoints: 12_500, description: 'Built-up grime, needs extra passes' },
  { code: 'extreme', label: 'Extreme', basisPoints: 15_000, description: 'Post-construction, move-out, neglected' },
] as const satisfies readonly MultiplierOption[];

export type RoomSizeCode = (typeof ROOM_SIZES)[number]['code'];
export type RoomConditionCode = (typeof ROOM_CONDITIONS)[number]['code'];

export const ROOM_SIZE_CODES = ROOM_SIZES.map((s) => s.code) as readonly RoomSizeCode[];
export const ROOM_CONDITION_CODES = ROOM_CONDITIONS.map((c) => c.code) as readonly RoomConditionCode[];

export function sizeMultiplierFor(code: string): number {
  return ROOM_SIZES.find((s) => s.code === code)?.basisPoints ?? BASIS_POINTS_SCALE;
}

export function conditionMultiplierFor(code: string): number {
  return ROOM_CONDITIONS.find((c) => c.code === code)?.basisPoints ?? BASIS_POINTS_SCALE;
}

// ---------------------------------------------------------------------------
// Billing increments (spec §9.8)
// ---------------------------------------------------------------------------

export const BILLING_INCREMENTS = [
  { minutes: 1, label: 'Exact minute' },
  { minutes: 5, label: '5 minutes' },
  { minutes: 15, label: '15 minutes' },
  { minutes: 30, label: '30 minutes' },
  { minutes: 60, label: 'Full hour' },
] as const;

export const BILLING_INCREMENT_MINUTES = BILLING_INCREMENTS.map((b) => b.minutes) as readonly number[];

// ---------------------------------------------------------------------------
// Crew size (0109)
// ---------------------------------------------------------------------------

/**
 * The most people one quote can plan to send. Matches the column's own CHECK;
 * far above any real cleaning crew, so the cap only ever catches typos.
 */
export const MAX_CREW_SIZE = 50;

// ---------------------------------------------------------------------------
// Quote status machine (spec §22)
// ---------------------------------------------------------------------------

export const QUOTE_STATUSES = [
  'draft',
  'ready',
  'sent',
  'viewed',
  'accepted',
  'declined',
  'expired',
  'completed',
  'cancelled',
] as const;

export type QuoteStatus = (typeof QUOTE_STATUSES)[number];

/** Allowed status transitions, exactly as enumerated in spec §22. */
export const QUOTE_STATUS_TRANSITIONS: Readonly<Record<QuoteStatus, readonly QuoteStatus[]>> = {
  draft: ['ready', 'cancelled'],
  ready: ['sent', 'cancelled'],
  sent: ['viewed', 'accepted', 'declined', 'cancelled'],
  viewed: ['accepted', 'declined'],
  accepted: ['completed'],
  declined: [],
  expired: [],
  completed: [],
  cancelled: [],
};

export function canTransition(from: QuoteStatus, to: QuoteStatus): boolean {
  return QUOTE_STATUS_TRANSITIONS[from].includes(to);
}

/**
 * Statuses that a passed expiration date actually changes.
 *
 * Expiry is derived, never stored. A quote that reached a terminal outcome has
 * one — declined is declined, canceled is canceled — and relabeling it
 * "Expired" once the date passes hides what really happened. The SQL filter
 * excludes the same set, so the badge and the list agree.
 */
export const EXPIRABLE_QUOTE_STATUSES: readonly QuoteStatus[] = ['draft', 'ready', 'sent', 'viewed'];

export function isExpirable(status: QuoteStatus): boolean {
  return EXPIRABLE_QUOTE_STATUSES.includes(status);
}

/**
 * Statuses staff may accept a quote *from* when the customer has already said
 * yes somewhere the app was not — on the doorstep, on the phone, in a text.
 *
 * The machine only allows sent → accepted and viewed → accepted, because the
 * acceptance it models is the customer's own click on the share link. That
 * leaves the commonest close in this trade with no path at all: an estimator
 * walks the house, prices it on the phone, the customer says "book it", and
 * the quote is still a draft that nobody is going to email to somebody
 * standing in front of them. Widening the machine itself would let a customer
 * accept an unfinished draft through the public page; this set is deliberately
 * separate, reachable only by a staff member holding `quotes:write`, and every
 * use of it is written into the audit trail as accepted without sending.
 *
 * It is the open statuses and only those: a declined, canceled or already
 * closed quote still needs the override, with its mandatory reason.
 */
export const STAFF_ACCEPTABLE_QUOTE_STATUSES: readonly QuoteStatus[] = ['draft', 'ready', 'sent', 'viewed'];

export function canStaffAccept(status: QuoteStatus): boolean {
  return STAFF_ACCEPTABLE_QUOTE_STATUSES.includes(status);
}

/**
 * Why a quote was declined, as something countable.
 *
 * The customer has always been able to write a sentence with their decline
 * (`declined_message`), and that is the more informative field — it is also
 * usually blank, and never the same shape twice, so "why are we losing work?"
 * was a question nothing could answer. These five are the answers a cleaning
 * company can actually act on: the first two are a price and a calendar
 * problem it could fix, the third is a competitor, the fourth is nobody's
 * fault, and the last exists so somebody who does not fit the other four is
 * not pushed into one that flatters us.
 *
 * NULL — no reason recorded — is a sixth state and is not in this list, on
 * purpose: it is what every decline before this feature has, and what a
 * customer who skips the question produces. The report counts it as "not
 * given" rather than folding it into "other", which would claim they answered.
 *
 * Adding to this list is safe: the column has no CHECK constraint (see
 * migration 0076) precisely so the set can grow without a table rebuild.
 */
export const QUOTE_DECLINE_REASONS = ['price', 'timing', 'chose_another', 'no_longer_needed', 'other'] as const;

export type QuoteDeclineReason = (typeof QUOTE_DECLINE_REASONS)[number];

export function isQuoteDeclineReason(value: string): value is QuoteDeclineReason {
  return (QUOTE_DECLINE_REASONS as readonly string[]).includes(value);
}

/**
 * Statuses a privileged user may set directly, outside the machine.
 *
 * Everything except `expired`, which is derived from the expiration date and
 * never stored — writing it would create a quote whose badge disagrees with
 * its date. To expire a quote manually, move its expiration date instead.
 */
export const MANUAL_QUOTE_STATUSES = QUOTE_STATUSES.filter((status) => status !== 'expired') as [
  QuoteStatus,
  ...QuoteStatus[],
];

/** Statuses that are terminal for the customer — editing requires a revision. */
export const LOCKED_QUOTE_STATUSES: readonly QuoteStatus[] = ['accepted', 'completed'];

/** Statuses a quote may no longer be edited from at all. */
export const CLOSED_QUOTE_STATUSES: readonly QuoteStatus[] = ['declined', 'cancelled', 'completed'];

export const QUOTE_STATUS_LABELS: Readonly<Record<QuoteStatus, string>> = {
  draft: 'Draft',
  ready: 'Ready to send',
  sent: 'Sent',
  viewed: 'Viewed',
  accepted: 'Accepted',
  declined: 'Declined',
  expired: 'Expired',
  completed: 'Completed',
  cancelled: 'Canceled',
};

// ---------------------------------------------------------------------------
// Booked quotes (migration 0117)
// ---------------------------------------------------------------------------

/**
 * What a quote is *called*, which is not always what it stores.
 *
 * Two of these are derived rather than written. `expired` has always been:
 * nothing writes that status, it is what a passed `expirationDate` makes of
 * an open quote (see EXPIRABLE_QUOTE_STATUSES). `scheduled` is the same idea
 * one step further along — an accepted quote that has been turned into a
 * schedule, which the quote knows because the booking stamped the series onto
 * it (`scheduledSeriesId`).
 *
 * Deriving beats storing for both, and for the same reason: the fact and the
 * label cannot drift apart. A quote whose expiration date moves stops being
 * expired without anybody running a job; a quote whose schedule is deleted
 * stops being scheduled the moment the link clears. Neither can be left
 * saying something the row no longer supports.
 */
export const QUOTE_DISPLAY_STATUSES = [...QUOTE_STATUSES, 'scheduled'] as const;

export type QuoteDisplayStatus = (typeof QUOTE_DISPLAY_STATUSES)[number];

export const QUOTE_DISPLAY_STATUS_LABELS: Readonly<Record<QuoteDisplayStatus, string>> = {
  ...QUOTE_STATUS_LABELS,
  scheduled: 'Scheduled',
};

/**
 * The quote as anything that shows a badge should read it.
 *
 * Order is the point. A booked quote is `scheduled` whatever else is true of
 * it — being on the calendar outranks the acceptance that got it there, and
 * outranks expiry too, since `isExpired` only ever fires on the open statuses
 * and a booked quote left those behind. Everything else falls through to
 * expiry and then to the stored status, exactly as before.
 *
 * Every surface goes through this rather than testing the fields itself, so
 * the list, the editor, the mobile row and the PDF cannot disagree about what
 * one quote is.
 */
export function quoteDisplayStatus(quote: {
  status: QuoteStatus | string;
  isExpired?: boolean;
  scheduledSeriesCount?: number;
  visitsPerWeek?: number | null;
  seriesClaimCeiling?: number | null;
}): QuoteDisplayStatus {
  const status = quote.status as QuoteStatus;
  // Only an ACCEPTED quote is described by its bookings. Every terminal
  // status outranks them and says so itself: a quote the office marked
  // `completed` has had its work done, one that was canceled or declined out
  // of `accepted` did not go ahead — and the schedules stay on the row either
  // way, since they really were booked. Badging those "Scheduled" would hide
  // the outcome behind the step before it.
  if (status === 'accepted' && isScheduledQuote(quote)) return 'scheduled';
  if (quote.isExpired && isExpirable(status)) return 'expired';
  return status;
}

/**
 * How many schedules this quote promises.
 *
 * One, for a quote booked after a series learned to name every weekday it
 * visits. A contract sold at three visits a week is one series (Monday,
 * Wednesday, Friday), not three.
 *
 * `seriesClaimCeiling` is the exception recorded for a quote that was already
 * partly booked the old way — one series per weekday, and not all of them yet.
 * Those outstanding series stay bookable. Absent, the ceiling is one.
 */
export function seriesPromisedBy(quote?: {
  visitsPerWeek?: number | null;
  seriesClaimCeiling?: number | null;
}): number {
  const ceiling = quote?.seriesClaimCeiling;
  if (ceiling != null && ceiling > 1) return Math.min(7, Math.trunc(ceiling));
  return 1;
}

/**
 * Whether this quote has been booked.
 *
 * A quote backs one schedule, unless it still owes the weekday series it was
 * partly booked as before that change. The visits one schedule repeats on —
 * one weekday, or Monday and Thursday and Friday — are part of that one rule.
 * Selling the same work again is what a duplicate is for.
 */
export function isScheduledQuote(quote: {
  scheduledSeriesCount?: number;
  visitsPerWeek?: number | null;
  seriesClaimCeiling?: number | null;
}): boolean {
  return (quote.scheduledSeriesCount ?? 0) >= seriesPromisedBy(quote);
}

/**
 * Whether a schedule at this frequency can be the one a quote promises.
 *
 * A monthly price covering several visits a week sells a weekly schedule. A
 * one-off, every-two-weeks or monthly schedule filed against it (a deep clean
 * hung off the contract, say) is not that schedule: it spends none of the
 * quote's slots, so the quote stays bookable for the weekly visits it sold,
 * and it is not priced as a share of the month (issue #280). Weekly means
 * every week: a weekly rule set to repeat every two or more weeks is not it.
 */
export function frequencyFillsQuoteSlot(
  frequency: string,
  visitsPerWeek: number | null | undefined,
  intervalWeeks?: number | null,
): boolean {
  return !((visitsPerWeek ?? 1) > 1 && !(frequency === 'weekly' && (intervalWeeks ?? 1) === 1));
}

/**
 * Whether a quote has a schedule and still owes another one.
 *
 * False for a quote that promises one schedule, before and after that schedule
 * exists. True only for a grandfathered quote that still has a weekday series
 * left to place.
 */
export function isPartlyScheduledQuote(quote: {
  scheduledSeriesCount?: number;
  visitsPerWeek?: number | null;
  seriesClaimCeiling?: number | null;
}): boolean {
  return (quote.scheduledSeriesCount ?? 0) > 0 && !isScheduledQuote(quote);
}

// ---------------------------------------------------------------------------
// App subscription (the plans companies pay for to use the app)
// ---------------------------------------------------------------------------

/** Days of full access a new company gets before subscribing. */
export const SUBSCRIPTION_TRIAL_DAYS = 14;

/**
 * Every tier, cheapest first. `free` is the one nobody pays for and the one
 * no gateway knows about: see PAID_PLAN_IDS below for the ones that carry a
 * price, and `PLANS.free` for what it does and does not include.
 *
 * Order matters — `minimumPlanFor` walks this list and answers with the first
 * tier carrying a feature, so a cheaper tier must never come after a dearer
 * one.
 */
export const PLAN_IDS = ['free', 'starter', 'pro', 'business'] as const;
export type PlanId = (typeof PLAN_IDS)[number];

/**
 * The tiers with a price, which is to say the ones the gateway has a product
 * for. Free is deliberately absent: there is no Stripe price to check out
 * against, no subscription to change, and nothing to bill — a company lands
 * on it by choosing it (POST /billing/free), not by paying for it.
 *
 * Anything that talks to the gateway — the price catalogs in worker/env.ts,
 * Checkout, plan changes — is typed on this narrower set, so "sell the Free
 * plan" is a type error rather than a runtime surprise.
 */
export const PAID_PLAN_IDS = ['starter', 'pro', 'business'] as const;
export type PaidPlanId = (typeof PAID_PLAN_IDS)[number];

/**
 * Plan-gated capabilities. A feature listed on a plan is available to
 * companies on that plan; a trialing company that has not chosen a plan yet
 * gets every feature (the trial is a trial of everything).
 *
 * The first five exist because of the Free plan, which is the whole of the
 * app narrowed to two jobs — keep a customer list, and build a quote by hand
 * — and therefore has to be able to withhold everything else. Every PAID plan
 * carries all five, so on a deployment that only ever sells paid plans they
 * gate nothing; they are the seam between "free" and "a business runs on it".
 *
 * Deliberately NOT on this list, because every plan including Free has them
 * and a gate every plan passes is dead code:
 *
 *  - Customers, properties and manual quotes: the room-by-room estimator, the
 *    room and service libraries it prices from, quote PDFs, emailing a quote,
 *    and the customer-facing quote page where it is accepted. This is what
 *    Free IS.
 *  - Customer payments. Charging the company's own customers through its
 *    own Stripe account, linked through Stripe Connect: card-on-file,
 *    pay-by-link, automatic billing, refunds. There is no platform fee on
 *    any of it; a company pays Stripe's own fees and nothing to this app.
 *    (Distinct from the app's OWN subscription billing, which is also never
 *    plan-gated and runs on the deployment's Stripe account.) The INVOICES
 *    those payments settle are gated — `invoicing` below — but no plan is
 *    ever charged extra for taking money.
 *
 * One exception to that rule is on the list anyway: `quote_payments`, which
 * every plan carries, Free included. It names the slice of the payments API
 * a company with no invoices still reaches (see its own comment), so those
 * routes say what they ride instead of riding `invoicing` by default, and it
 * reaches clients through `planFeatures`, which is how an app built against
 * an older server tells that this one records payments on quotes.
 */
export const PLAN_FEATURES = [
  'live_map',
  // The calendar: appointments, recurring series, work orders, availability,
  // visit confirmations and reminders, and the subscribed calendar feeds.
  'scheduling',
  // Invoices and everything downstream of one: the invoice list and PDFs, the
  // customer portal, pay pages, and recording a payment against a visit.
  'invoicing',
  // The unified inbox — customer email and text conversations in one thread.
  'messaging',
  'job_chat',
  'staff_portal',
  // The base reporting tier: the owner dashboard, receivables, and each
  // person's My Week. The two tiers below gate the rest — every PAID plan
  // reports, higher plans analyze more.
  'reports_core',
  // Lead capture: the public quote-request form, the embeddable website
  // widget, and the requests queue they arrive in.
  'online_intake',
  // Turning OFF the "Powered by …" line that every customer-facing quote
  // carries by default (migration 0073). Not the branding itself — that is on
  // for everybody until somebody switches it off — but the switch. A company
  // paying nothing advertises the app it is not paying for; a company paying
  // for it may decide its own quotes look like its own.
  'unbranded_quotes',
  // Text-only AI-assisted quote intake. It reuses the deterministic quote
  // engine but not the Pro-only Magic walkthrough/media entitlement.
  'quick_quote',
  // Outbound SMS of any kind (reminders, quote texts, alerts).
  'sms',
  // Calls to the company's BlastoClean number: forwarding, voicemail with its
  // recording and transcript in the inbox, and the AI pass on a voicemail.
  // Pro and up, beside `sms`: it rides the same per-company number. The
  // deployment flag FEATURE_VOICE_INBOX must also be on.
  'voice_inbox',
  // The "better" reporting tier: profitability, quote funnel, crew and lead
  // reports.
  'reports_analytics',
  // The "best" reporting tier: retention/win-back and capacity planning.
  'reports_advanced',
  // More than one location, and the per-location add-on price.
  'multi_location',
  // Business skips the per-company daily SMS cap (abuse caps still apply).
  'unlimited_sms',
  // Business skips the per-quote attachment count/size caps.
  'unlimited_storage',
  // API keys, OAuth access tokens, and anything riding on them (Zapier, Make,
  // a bespoke script) — the whole external-API surface described in
  // docs/api.md and docs/integrations.md. Not AI assistants: see below.
  'external_api',
  // ChatGPT, Claude and other AI assistants connected through the MCP server
  // at /mcp. On every PAID plan, where `external_api` is Pro: an assistant's
  // grant is capped at drafts and reads (AI_ASSISTANT_SCOPES) and each person
  // connects their own, limited by their role, so it is a way of using the
  // app rather than an integration surface to build on. Free has none.
  'ai_assistants',
  // Magic Quote walkthroughs. The one AI feature that IS plan-gated, because
  // it is the one whose unit of work is a whole property rather than a single
  // message: a walkthrough reads every photo and recording separately, so it
  // spends AI actions by the dozen where every other assist spends one. See
  // docs/ai-credits.md.
  'magic_quote',
  // Required photos on a checklist task, the customer checklist report, and
  // completion counts the office shares. Ticking a task is scheduling.
  'checklist_reports',
  // Recording what a customer paid on an accepted quote: the payment types
  // list, recording and refunding a payment on a quote, its receipt, the
  // customer's history of those payments, and what is still owed on accepted
  // quotes. On EVERY plan, Free included — a solo crew quotes the job, gets
  // paid and writes it down. Visits, invoices, cards and Stripe stay behind
  // `invoicing`.
  'quote_payments',
  // One job at a time, from an approved one-time quote: booking its one visit
  // (the same appointment the paid schedule writes), changing or canceling
  // it, marking it completed, a Stripe Checkout payment link for the balance
  // or a manual payment of the whole balance on that visit, Book again, and
  // the Booked list of upcoming jobs. Stripe Connect onboarding rides it too,
  // since the payment link needs an account. On EVERY plan, Free included.
  // The calendar, recurring jobs, dispatch, visits not tied to a quote, the
  // invoice list, reminders, reports, card on file and Tap to Pay stay behind
  // `scheduling` and `invoicing`.
  'single_job',
  // What each job made after labor: a team member's worker type, pay rates
  // and W-9 status, and the Job profit card on a visit. On every paid plan,
  // because once there is a crew "did that job make money?" is a basic
  // question rather than an analysis.
  'job_profit',
  // Everything built on top of those numbers: the job profit report and its
  // customer roll-up, the target margin and the quote warning it drives,
  // supplies as a cost, the accountant export and the 1099 year-end summary.
  'profit_insights',
  // Earlier-visit offers (web #436): when a visit is canceled, customers who
  // asked for an earlier time are offered it, by email and by text where
  // texting is set up. Every PAID plan, Starter included; never Free, whose
  // customers did not sign up for a waiting list (Chris, Oct 7 2026). Gates
  // the cancel trigger, the offer rounds, the Fill open spots switch, the
  // office approve/decline routes and every place a customer can opt in.
  'earlier_visit_offers',
] as const;
export type PlanFeature = (typeof PLAN_FEATURES)[number];

export interface PlanDefinition {
  id: PlanId;
  name: string;
  /** Monthly base price, in cents. */
  basePriceCents: number;
  /** Active users covered by the base price. */
  includedSeats: number;
  /** Monthly price per active user beyond includedSeats, in cents. */
  extraSeatPriceCents: number;
  /** Locations covered by the base price (always 1 today). */
  includedLocations: number;
  /**
   * Monthly price per active location beyond includedLocations, in cents.
   * null = additional locations are not purchasable on this plan; the company
   * must upgrade to a plan that carries the multi_location feature.
   */
  extraLocationPriceCents: number | null;
  /**
   * The most active users this plan allows at all, or null when extra users
   * are simply billed (every paid plan). Only Free sets a ceiling, because it
   * is the only plan with no per-seat price to charge past its allowance.
   *
   * A ceiling stops a seat being ADDED; it never takes one away. A company
   * arriving on Free with a bigger team keeps every account it already has —
   * see `assertSeatAvailable` in worker/services/entitlements.ts.
   */
  maxSeats: number | null;
  features: readonly PlanFeature[];
}

/**
 * What every plan carries, Free included. Kept as a list because the plans
 * spread it, so a feature added here reaches every tier at once.
 */
const EVERY_PLAN_FEATURES = ['quote_payments', 'single_job'] as const;

/** What a paid plan carries before its own tier adds anything: the app. */
const STARTER_FEATURES = [
  'live_map',
  ...EVERY_PLAN_FEATURES,
  'scheduling',
  'invoicing',
  'messaging',
  'job_chat',
  'reports_core',
  'online_intake',
  'unbranded_quotes',
  'quick_quote',
  'job_profit',
  'ai_assistants',
  'earlier_visit_offers',
] as const;
const PRO_FEATURES = [
  'staff_portal',
  ...STARTER_FEATURES,
  'sms',
  'voice_inbox',
  'reports_analytics',
  'external_api',
  'magic_quote',
  'checklist_reports',
  'profit_insights',
] as const;

export const PLANS: Readonly<Record<PlanId, PlanDefinition>> = {
  /**
   * Customers and manual quotes, for one person, forever, at no charge.
   *
   * The feature list is what every plan carries and nothing more, and that is
   * the entire definition: a company on Free keeps its customers and
   * properties, builds quotes room by room out of its own libraries, emails
   * them, has them accepted on the same customer-facing page every other plan
   * uses, and records what the customer paid on an accepted quote
   * (`quote_payments`). Everything a business runs on past that point — the
   * calendar, invoices and card payments, the inbox, reports, lead capture —
   * is a paid feature and is refused with `403`/`PLAN_REQUIRED` naming the
   * cheapest plan that carries it.
   *
   * The trial is 14 days of everything. Free is where a company can land when
   * the trial ends, when a subscription lapses, or when an owner chooses it.
   * Choosing it during a live trial ends that trial immediately.
   */
  free: {
    id: 'free',
    name: 'Free',
    basePriceCents: 0,
    includedSeats: 1,
    // Nothing to charge for a seat this plan does not sell; maxSeats is what
    // actually holds the line.
    extraSeatPriceCents: 0,
    includedLocations: 1,
    extraLocationPriceCents: null,
    maxSeats: 1,
    features: EVERY_PLAN_FEATURES,
  },
  starter: {
    id: 'starter',
    name: 'Starter',
    basePriceCents: 2900,
    includedSeats: 3,
    extraSeatPriceCents: 300,
    includedLocations: 1,
    extraLocationPriceCents: null,
    maxSeats: null,
    features: STARTER_FEATURES,
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    basePriceCents: 6900,
    includedSeats: 6,
    extraSeatPriceCents: 500,
    includedLocations: 1,
    extraLocationPriceCents: null,
    maxSeats: null,
    features: PRO_FEATURES,
  },
  business: {
    id: 'business',
    name: 'Business',
    basePriceCents: 14900,
    includedSeats: 15,
    extraSeatPriceCents: 500,
    includedLocations: 1,
    extraLocationPriceCents: 4500,
    maxSeats: null,
    features: [...PRO_FEATURES, 'reports_advanced', 'multi_location', 'unlimited_storage'],
  },
};

/** The plan a company lands on when it pays for none: Free. */
export const FREE_PLAN_ID: PlanId = 'free';

/**
 * The plan a company lands on when nothing else decides one — the cheapest
 * PAID tier, not Free. It stands in for a stored plan that is missing or
 * unrecognized, and reaching for Free there would hand entitlements away on
 * the strength of a bad row rather than a decision anybody made.
 */
export const DEFAULT_PLAN_ID: PaidPlanId = 'starter';

/**
 * The plan whose entitlements a trialing company enjoys before choosing one:
 * the trial unlocks everything, so it behaves like the top plan.
 */
export const TRIAL_PLAN_ID: PaidPlanId = 'business';

/**
 * The plan the app points people to.
 *
 * Not the cheapest way past any particular gate — `minimumPlanFor` answers
 * that, and answers it honestly — but the tier the product is built around:
 * Pro is where Magic Quote, texting and the analytics reports live, which is
 * to say where the app stops being a quote pad and starts running the office.
 * Every upgrade prompt names the minimum first (nobody should be sold more
 * than they asked for) and mentions this second.
 *
 * One constant rather than a mention per screen, so a change of strategy is a
 * change of line rather than a hunt through the interface.
 */
export const RECOMMENDED_PLAN_ID: PaidPlanId = 'pro';

/** Where a plan sits in the catalog: Free 0, Starter 1, Pro 2, Business 3. */
export function planRank(plan: PlanId): number {
  return PLAN_IDS.indexOf(plan);
}

/** Whether `plan` is a lower tier than `other` — "is there something to sell". */
export function planRanksBelow(plan: PlanId, other: PlanId): boolean {
  return planRank(plan) < planRank(other);
}

/**
 * The paid tier a gateway write should use for a plan that may be Free.
 *
 * Nothing bills Free, so the gateway code is typed to exclude it; this is the
 * one narrowing point, for the paths that derive a plan from entitlements
 * (which can answer 'free') and then have to tell Stripe something.
 */
export function asPaidPlanId(plan: PlanId): PaidPlanId {
  return plan === 'free' ? DEFAULT_PLAN_ID : plan;
}

export function isPlanId(value: unknown): value is PlanId {
  return typeof value === 'string' && (PLAN_IDS as readonly string[]).includes(value);
}

/** A plan the gateway can actually sell — everything except Free. */
export function isPaidPlanId(value: unknown): value is PaidPlanId {
  return typeof value === 'string' && (PAID_PLAN_IDS as readonly string[]).includes(value);
}

export function planHasFeature(plan: PlanId, feature: PlanFeature): boolean {
  return PLANS[plan].features.includes(feature);
}

/**
 * The cheapest plan carrying a feature, for upgrade prompts.
 *
 * Free carries only what every plan carries, so it is never the answer for a
 * feature somebody can be missing — which is the point: every prompt this
 * feeds names something a company can actually buy. (It answers Free for
 * `quote_payments`, which no prompt asks about, because no plan lacks it.)
 */
export function minimumPlanFor(feature: PlanFeature): PlanId {
  for (const id of PLAN_IDS) {
    if (PLANS[id].features.includes(feature)) return id;
  }
  return 'business';
}

/** Whether this plan would allow one more active user than `activeSeats`. */
export function planAllowsAnotherSeat(plan: PlanId, activeSeats: number): boolean {
  const ceiling = PLANS[plan].maxSeats;
  return ceiling === null || activeSeats < ceiling;
}

/**
 * trialing = inside the free trial, no gateway subscription yet.
 * The other three mirror the gateway subscription's state.
 */
export const BILLING_STATUSES = ['trialing', 'active', 'past_due', 'canceled'] as const;
export type BillingStatus = (typeof BILLING_STATUSES)[number];

/**
 * What the subscription back end can and cannot do, serialized into
 * `BillingState` so the routes and the UI adapt to facts rather than
 * hard-coding a gateway's behavior. App subscriptions always bill through the
 * deployment's own Stripe account — this describes what that integration
 * supports.
 */
export interface SubscriptionCapabilities {
  /**
   * The gateway hosts a page that manages the whole subscription — card,
   * invoices, cancellation (Stripe's Customer Portal).
   */
  hostedPortal: boolean;
  /** The app itself offers cancellation (the Customer Portal owns it instead). */
  inAppCancel: boolean;
  /** Mid-cycle plan changes are prorated by the gateway. */
  prorates: boolean;
  /**
   * The card is tokenized in the browser by the gateway's own iframes. False
   * means the card is entered on a page the gateway serves and the app never
   * renders a payment field at all (Stripe Checkout). Either way no card
   * number reaches this Worker — see README "Where card data goes".
   */
  clientSideTokenization: boolean;
  /**
   * The back end can bill yearly (a second price catalog at 10× monthly).
   * Whether a given DEPLOYMENT offers it also needs the yearly price ids
   * configured — `BillingState.yearlyAvailable` is the per-deployment answer.
   */
  yearlyBilling: boolean;
  /** The back end supports subscription discounts (coupons / promo codes). */
  discounts: boolean;
}

/**
 * Ways a company can pay for the app, named once so the deployment setting,
 * the API and the checkout UI all mean the same thing by "paypal".
 *
 * The list is what may be asked for, not what will be shown: wallets ride on
 * `card` in Stripe Checkout and appear automatically on a supporting device,
 * and which methods appear at all depends on what the gateway account has
 * activated.
 *
 * Bank debit (ACH) is deliberately absent. It was offered by an earlier
 * draft and removed: a mandate is a different consent story from a card, its
 * failures arrive days later rather than at the moment of purchase, and none
 * of that is worth carrying for a monthly SaaS fee.
 */
export const SUBSCRIPTION_PAYMENT_METHODS = ['card', 'paypal', 'apple_pay', 'google_pay'] as const;
export type SubscriptionPaymentMethod = (typeof SUBSCRIPTION_PAYMENT_METHODS)[number];

// ---------------------------------------------------------------------------
// Roles and permissions (spec §6.4)
// ---------------------------------------------------------------------------

export const USER_ROLES = ['owner', 'manager', 'estimator', 'read_only', 'crew'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const PERMISSIONS = [
  'company:read',
  'company:write',
  'company:export',
  'company:import',
  'company:delete',
  'users:manage',
  // Minting and revoking the company's API keys. Owner-only, and deliberately
  // its own permission rather than folded into `company:write`: a key is a
  // standing credential over the company's data, which is a different kind of
  // decision from changing a setting.
  'api_keys:manage',
  'customers:read',
  'customers:write',
  'customers:delete',
  'quotes:read',
  'quotes:write',
  'quotes:delete',
  'quotes:price_override',
  'quotes:status_override',
  'templates:read',
  'templates:write',
  'files:write',
  'pdf:generate',
  'reports:read',
  'logs:read',
  'schedule:read',
  'schedule:write',
  'checklists:write',
  'checklists:override',
  'visit_photos:write',
  'staff_portal:read',
  'staff_portal:write',
  'staff_portal:manage',
  'payments:read',
  'payments:write',
  'payments:refund',
  // The feature feedback board (spec §39). Held by every role including
  // read_only, deliberately: the board is where a customer tells us what to
  // build, and an estimator who spends all day in the app has more to say
  // about it than the owner who signs the invoice. Nothing here reaches any
  // company data — a feedback row carries a title, a description and a vote.
  'feedback:view',
  'feedback:submit',
  'feedback:vote',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export const ROLE_PERMISSIONS: Readonly<Record<UserRole, readonly Permission[]>> = {
  // Full access.
  owner: PERMISSIONS,

  // Everything an owner can do except owning the company itself: its
  // settings, its users, and the delivery logs that carry customer contact
  // details company-wide.
  manager: [
    'checklists:override',
    'staff_portal:read',
    'staff_portal:write',
    'staff_portal:manage',
    'company:read',
    'customers:read',
    'customers:write',
    'customers:delete',
    'quotes:read',
    'quotes:write',
    'quotes:delete',
    'quotes:price_override',
    'quotes:status_override',
    'templates:read',
    'templates:write',
    'files:write',
    'pdf:generate',
    'reports:read',
    'schedule:read',
    'schedule:write',
    'payments:read',
    'payments:write',
    'payments:refund',
    'feedback:view',
    'feedback:submit',
    'feedback:vote',
  ],

  // Builds quotes but cannot change company-wide service pricing — and can see
  // payment state but never move money.
  estimator: [
    'staff_portal:read',
    'staff_portal:write',
    'company:read',
    'customers:read',
    'customers:write',
    'quotes:read',
    'quotes:write',
    'templates:read',
    'files:write',
    'pdf:generate',
    'schedule:read',
    'schedule:write',
    'payments:read',
    'feedback:view',
    'feedback:submit',
    'feedback:vote',
  ],

  // Explicitly assigned visits only. The server's Crew surface and assignment
  // guards constrain schedule:read; these task writes do not grant dispatch.
  crew: ['schedule:read', 'checklists:write', 'visit_photos:write', 'staff_portal:read', 'staff_portal:write'],

  // Views and prints; modifies nothing. PDF generation is excluded because it
  // writes an object to R2 and stamps the quote row.
  read_only: [
    'staff_portal:read',
    'company:read',
    'customers:read',
    'quotes:read',
    'templates:read',
    'schedule:read',
    'payments:read',
    'feedback:view',
    'feedback:submit',
    'feedback:vote',
  ],
};

export function roleHasPermission(role: UserRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

export const ROLE_LABELS: Readonly<Record<UserRole, string>> = {
  owner: 'Owner',
  manager: 'Manager',
  estimator: 'Estimator',
  read_only: 'Read only',
  crew: 'Crew',
};

// ---------------------------------------------------------------------------
// Team pay and job profit (migration 0035)
// ---------------------------------------------------------------------------

/**
 * How a team member is employed, as far as their cost goes. A W-2 employee
 * costs their pay plus the employer's payroll load (taxes, workers' comp,
 * unemployment insurance); a 1099 contractor costs exactly what they are
 * paid. Unset (null on the row) means their time is not costed yet, and the
 * visit says so rather than guessing.
 *
 * Informational only: the label does not make anybody an employee or a
 * contractor, and the app says so next to the setting.
 */
export const WORKER_TYPES = ['w2', '1099'] as const;
export type WorkerType = (typeof WORKER_TYPES)[number];

/**
 * How a team member is paid, one rate at a time, each from an effective date.
 *
 *   * `hourly` — cents per clocked hour.
 *   * `per_job` — a flat amount per completed visit, each worker their own.
 *   * `percent` — a share of the job price, after discounts and before sales
 *     tax and tips.
 *   * `salary` — W-2 only: an annual salary, costed per hour at salary over
 *     the hours it covers (2,080 by default) and labeled approximate.
 */
export const PAY_TYPES = ['hourly', 'per_job', 'percent', 'salary'] as const;
export type PayType = (typeof PAY_TYPES)[number];

export function isWorkerType(value: unknown): value is WorkerType {
  return typeof value === 'string' && (WORKER_TYPES as readonly string[]).includes(value);
}

export function isPayType(value: unknown): value is PayType {
  return typeof value === 'string' && (PAY_TYPES as readonly string[]).includes(value);
}

/** Hours a full-time year covers: 52 weeks of 40. Editable per salary. */
export const DEFAULT_SALARY_HOURS_PER_YEAR = 2080;

/**
 * Stripe's standard online card price in the US — 2.9% plus 30 cents — as the
 * starting estimate of what a card payment costs the company. The real fee is
 * never stored (see `estimateCardProcessingCents`), so the owner can change
 * this in Settings → Team pay to match their own statement.
 */
export const DEFAULT_CARD_PROCESSING_BASIS_POINTS = 290;
export const DEFAULT_CARD_PROCESSING_FIXED_CENTS = 30;

/** The IRS pages the W-9 reminder and the worker-type note link to. */
export const IRS_W9_URL = 'https://www.irs.gov/forms-pubs/about-form-w-9';
export const IRS_WORKER_CLASSIFICATION_URL =
  'https://www.irs.gov/businesses/small-businesses-self-employed/independent-contractor-self-employed-or-employee';

// ---------------------------------------------------------------------------
// In-app notifications (migration 0108)
// ---------------------------------------------------------------------------

/**
 * What the bell can ring about. Each type carries its own audience rule at
 * the write site (services/notifications.ts) and its own icon and label in
 * the two clients — adding a type means touching all three, and this list is
 * what the clients switch over.
 */
export const NOTIFICATION_TYPES = [
  'staff_announcement',
  'staff_time_off',
  /** A shift swap was asked, accepted, decided, canceled or expired (0095). */
  'staff_shift_swap',
  'visit_reassigned',
  /** A customer's payment did not go through. */
  'payment_failed',
  /** The company's own subscription card was declined. */
  'billing_past_due',
  /** A customer texted back. */
  'inbound_text',
  /** A customer replied to one of the company's emails. */
  'inbound_email',
  /** A caller left a voicemail on the company's number. */
  'voicemail',
  /** A lead arrived through the public intake form. */
  'new_lead',
  'quote_accepted',
  'quote_declined',
  /** A visit ended but was never marked completed. */
  'visit_overdue',
  /** A customer asked to move a visit. */
  'change_request',
  /** A customer confirmed a visit from their own confirmation page. */
  'appointment_confirmed',
  /** A portal client raised a service request or complaint. */
  'service_request',
  /** A customer booked themselves another visit from their portal. */
  'customer_booking',
  /** A compliance document is approaching its expiry. */
  'compliance_expiring',
  /** A visit was canceled. Owners and managers, not the customer. */
  'visit_cancelled',
  /** The company's dedicated texting number can send. */
  'texting_number_ready',
  /** The carriers approved or rejected the texting registration filed in the company's own Twilio account. */
  'texting_registration_changed',
  /** The owner removed BlastoClean's Twilio Connect App in Twilio: texts stopped. */
  'twilio_connect_removed',
  /** A customer took an open spot and their next visit moved. */
  'slot_filled',
  /** A customer asked to take an open spot and the office has to approve it. */
  'slot_needs_approval',
  /** An open spot was offered and nobody took it. */
  'slot_still_open',
  /** A 1099 contractor with no W-9 on file was put on a visit or finished one. */
  'w9_needed',
  /** A customer's photo quote is ready for the office to review and send. */
  'photo_quote_ready',
  /** A photo quote went past the company's answer time. */
  'photo_quote_overdue',
  /** A visit was put on a crew member's schedule (0099: the crew's own notice). */
  'visit_assigned',
  /** A visit on a crew member's schedule moved to another time. */
  'visit_changed',
  /** An AI suggestion the person asked for is ready for them to approve. */
  'inbox_action_ready',
  /** A customer approved or declined extra work from its approval link (0104). */
  'extra_work_answered',
  /**
   * Staff visit offers (F04). Written by routes/staff-visit-offers.ts as
   * `staff_visit_offer_<event>` and listed here so every row the bell can hold
   * has a type the clients and the push categories know.
   */
  'staff_visit_offer_available',
  'staff_visit_offer_urgent',
  'staff_visit_offer_claimed',
  'staff_visit_offer_approved',
  'staff_visit_offer_declined',
  'staff_visit_offer_cancelled',
] as const;

export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

// ---------------------------------------------------------------------------
// Lock-screen push notifications (migration 0099, docs/push-notifications.md)
// ---------------------------------------------------------------------------

/**
 * What a person can switch on or off for their phone. Stable keys: the mobile
 * app shows them, uses them as Android channel ids and stores nothing else.
 */
export const PUSH_CATEGORIES = [
  'messages',
  'calls',
  'ai_actions',
  'team_requests',
  'quotes',
  'visits',
  'visit_offers',
  'shift_swaps',
  'announcements',
  'account',
  'other',
] as const;

export type PushCategory = (typeof PUSH_CATEGORIES)[number];

export function isPushCategory(value: unknown): value is PushCategory {
  return typeof value === 'string' && (PUSH_CATEGORIES as readonly string[]).includes(value);
}

/**
 * A type whose rows go both to the people who decide and to the people who
 * asked: a shift swap is a request to approve for a manager and news about
 * their own swap for the cleaner. `approver` applies to a recipient whose role
 * (at the company or any of their locations) holds `staff_portal:manage`.
 */
export interface SplitPushCategory {
  approver: PushCategory;
  staff: PushCategory;
}

/**
 * Which side of a split type one recipient's copy is for, said by the writer
 * that chose the recipient: a manager's own swap or time-off decision is
 * `staff` news to them, however many requests they approve for others.
 */
export type PushAudiencePurpose = keyof SplitPushCategory;

/**
 * Every bell type, mapped to the category its push belongs to. A `Record`, so
 * a type added to NOTIFICATION_TYPES without a category fails the build; the
 * unit test (tests/unit/push-categories.test.ts) checks the values too.
 */
export const NOTIFICATION_PUSH_CATEGORY: Readonly<Record<NotificationType, PushCategory | SplitPushCategory>> = {
  staff_announcement: 'announcements',
  staff_time_off: { approver: 'team_requests', staff: 'other' },
  staff_shift_swap: { approver: 'team_requests', staff: 'shift_swaps' },
  visit_reassigned: 'visits',
  payment_failed: 'account',
  billing_past_due: 'account',
  inbound_text: 'messages',
  inbound_email: 'messages',
  voicemail: 'calls',
  new_lead: 'other',
  quote_accepted: 'quotes',
  quote_declined: 'quotes',
  visit_overdue: 'visits',
  change_request: 'other',
  appointment_confirmed: 'other',
  service_request: 'other',
  customer_booking: 'other',
  compliance_expiring: 'account',
  visit_cancelled: 'visits',
  texting_number_ready: 'account',
  texting_registration_changed: 'account',
  twilio_connect_removed: 'account',
  slot_filled: 'other',
  slot_needs_approval: 'other',
  slot_still_open: 'other',
  w9_needed: 'other',
  photo_quote_ready: 'quotes',
  photo_quote_overdue: 'quotes',
  visit_assigned: 'visits',
  visit_changed: 'visits',
  inbox_action_ready: 'ai_actions',
  extra_work_answered: 'quotes',
  staff_visit_offer_available: 'visit_offers',
  staff_visit_offer_urgent: 'visit_offers',
  staff_visit_offer_claimed: 'visit_offers',
  staff_visit_offer_approved: 'visit_offers',
  staff_visit_offer_declined: 'visit_offers',
  staff_visit_offer_cancelled: 'visit_offers',
};

/**
 * Who a category is relevant to: a role that holds the permission, or one of
 * the listed roles. `null` = everyone. Mirrors the audiences the bell rows are
 * written with, so a category is listed exactly for the people who can get
 * its rows: crew never see `ai_actions`, `messages` or `team_requests`.
 */
export const PUSH_CATEGORY_AUDIENCE: Readonly<
  Record<PushCategory, { permission?: Permission; roles?: readonly UserRole[] } | null>
> = {
  messages: { permission: 'customers:write' },
  calls: { permission: 'customers:write' },
  ai_actions: { permission: 'customers:write' },
  team_requests: { permission: 'staff_portal:manage' },
  quotes: { permission: 'quotes:write' },
  visits: { permission: 'schedule:read' },
  visit_offers: { permission: 'staff_portal:read' },
  shift_swaps: { permission: 'staff_portal:write' },
  announcements: { permission: 'staff_portal:read' },
  account: { roles: ['owner', 'manager'] },
  other: null,
};

/** Categories whose push rings even a locked phone at once (Android high priority). */
export const HIGH_PRIORITY_PUSH_CATEGORIES: ReadonlySet<PushCategory> = new Set(['messages', 'calls', 'visits']);

/** Whether any of these roles (company role plus location overrides) makes the category relevant. */
export function pushCategoryRelevant(category: PushCategory, roles: Iterable<UserRole>): boolean {
  const audience = PUSH_CATEGORY_AUDIENCE[category];
  if (audience === null) return true;
  for (const role of roles) {
    if (audience.roles?.includes(role)) return true;
    if (audience.permission && roleHasPermission(role, audience.permission)) return true;
  }
  return false;
}

/** The categories listed for (and pushed to) a person holding these roles, in display order. */
export function pushCategoriesFor(roles: Iterable<UserRole>): PushCategory[] {
  const held = [...roles];
  return PUSH_CATEGORIES.filter((category) => pushCategoryRelevant(category, held));
}

/**
 * The category of one recipient's copy of a bell row; null for a type this
 * build does not know. A split type follows the `purpose` its writer gave the
 * copy; only a copy written without one is judged by the roles held.
 */
export function pushCategoryFor(
  type: string,
  roles: Iterable<UserRole>,
  purpose?: PushAudiencePurpose | null,
): PushCategory | null {
  if (!(NOTIFICATION_TYPES as readonly string[]).includes(type)) return null;
  const rule = NOTIFICATION_PUSH_CATEGORY[type as NotificationType];
  if (typeof rule === 'string') return rule;
  if (purpose) return rule[purpose];
  for (const role of roles) if (roleHasPermission(role, 'staff_portal:manage')) return rule.approver;
  return rule.staff;
}

// ---------------------------------------------------------------------------
// Property types
// ---------------------------------------------------------------------------

export const PROPERTY_TYPES = [
  'house',
  'apartment',
  'condo',
  'townhouse',
  'office',
  'retail',
  'medical',
  'vacation_rental',
  'other',
] as const;

export type PropertyType = (typeof PROPERTY_TYPES)[number];

export const OCCUPANCY_STATUSES = ['occupied', 'vacant', 'move_in', 'move_out', 'under_construction'] as const;
export type OccupancyStatus = (typeof OCCUPANCY_STATUSES)[number];

// ---------------------------------------------------------------------------
// Residential and commercial work
// ---------------------------------------------------------------------------

/** What kind of cleaning a company does — a Settings choice, not a plan. */
export const SERVICE_MODES = ['residential', 'commercial', 'both'] as const;
export type ServiceMode = (typeof SERVICE_MODES)[number];

/** Which kind of work one quote is. Fixed at creation, like its currency. */
export const QUOTE_SEGMENTS = ['residential', 'commercial'] as const;
export type QuoteSegment = (typeof QUOTE_SEGMENTS)[number];

/**
 * Home vs business for one customer — homes (residential) or offices/stores
 * (commercial). Independent of the company's serviceMode and of any quote's
 * segment. Defaults to residential; tax treatment is a later step.
 */
export const CUSTOMER_SERVICE_SEGMENTS = ['residential', 'commercial'] as const;
export type CustomerServiceSegment = (typeof CUSTOMER_SERVICE_SEGMENTS)[number];

/**
 * What a quote's total buys: one visit, or a month of the specified service.
 * 'per_visit' is every quote written before commercial mode existed.
 */
export const BILLING_BASES = ['per_visit', 'monthly'] as const;
export type BillingBasis = (typeof BILLING_BASES)[number];

/**
 * How often a task on a commercial quote is done. 'every_visit' is the
 * residential answer and the default everywhere; the rest are the vocabulary
 * a janitorial spec is written in ("trash nightly, carpets weekly, strip and
 * wax quarterly").
 */
export const SERVICE_FREQUENCIES = ['every_visit', 'weekly', 'biweekly', 'monthly', 'quarterly'] as const;
export type ServiceFrequency = (typeof SERVICE_FREQUENCIES)[number];

/**
 * How many times a year each frequency happens, which is the engine's whole
 * use for it. Per YEAR rather than per month so every weight is an integer:
 * "4.33 weeks a month" is exactly the kind of constant that rounds twice and
 * disagrees with itself, where 52/26/12/4 divided once by 12 at the money
 * step cannot. 'every_visit' is deliberately absent — its yearly count is
 * `visitsPerWeek × 52` and belongs to the quote, not the task.
 */
export const FREQUENCY_OCCURRENCES_PER_YEAR: Readonly<Record<Exclude<ServiceFrequency, 'every_visit'>, number>> = {
  weekly: 52,
  biweekly: 26,
  monthly: 12,
  quarterly: 4,
};

/**
 * How a task's minutes are arrived at.
 *
 * 'time' is every task ever written before commercial estimating: somebody
 * decided this job takes twelve minutes and typed twelve. The other two are
 * how a commercial bid is actually built — from a measured quantity and a
 * production rate, which is the trade's own arithmetic and the reason two
 * estimators bidding the same building land within a few dollars of each
 * other.
 *
 *   * 'area' — square feet of floor, against a rate in square feet per hour.
 *   * 'fixture' — restroom fixtures (toilets, urinals, sinks), against a rate
 *     in fixtures per hour. Restrooms are priced per fixture, not per square
 *     foot: a 400 sq ft restroom with twelve fixtures is not the same job as
 *     a 400 sq ft one with three.
 *
 * Both rates are "units per hour" on purpose. It is the unit the ISSA book
 * publishes, it is the unit cleaners quote at each other in, and it keeps one
 * column meaning one thing whichever basis is on the row.
 */
export const MEASUREMENT_BASES = ['time', 'area', 'fixture'] as const;
export type MeasurementBasis = (typeof MEASUREMENT_BASES)[number];

/**
 * What is underfoot in a measured area. Recorded on the area because it is
 * the single biggest thing production rates vary by — carpet vacuums at twice
 * the rate a tile floor mops at — and because the crew needs to know before
 * they load the van.
 *
 * Advisory to the engine, which only ever reads the rate actually stored on
 * the task. Its job is to pick sensible starting rates (see
 * `./production-rates.ts`) and to print on the spec.
 */
export const FLOOR_TYPES = ['carpet', 'resilient', 'hard', 'concrete', 'mixed'] as const;
export type FloorType = (typeof FLOOR_TYPES)[number];

/**
 * The largest floor area one line of a bid may claim, in square feet.
 *
 * Not a guess at how big buildings get — a distribution center clears a
 * million square feet — but a guard on a number that multiplies into minutes
 * and then into money. Ten million square feet is four times the largest
 * building in North America, so anything past it is a typo, and catching it
 * at the schema beats discovering it in a total.
 */
export const MAX_MEASURED_SQUARE_FEET = 10_000_000;

/** The most fixtures one task line may count. A stadium restroom has ~100. */
export const MAX_FIXTURE_COUNT = 5_000;

/**
 * The fastest and slowest production rates a task line may carry, in units
 * per hour.
 *
 * The floor is 1 because a rate of zero is not a slow job, it is a missing
 * answer, and dividing by it produces an infinity that has no business
 * reaching a price. The ceiling is far above the quickest thing in the ISSA
 * book (auto-scrubbing a clear warehouse floor, ~60,000 sq ft/hour) and is
 * there for the same reason the area cap is.
 */
export const MIN_PRODUCTION_RATE_PER_HOUR = 1;
export const MAX_PRODUCTION_RATE_PER_HOUR = 500_000;

/**
 * The commercial property types, used to default a new quote's segment when a
 * company does both kinds of work: quoting an office is commercial until
 * somebody says otherwise.
 */
export const COMMERCIAL_PROPERTY_TYPES: readonly PropertyType[] = ['office', 'retail', 'medical'];

/**
 * What kind of thing gets a crew into a building (0089).
 *
 * The split that matters is codes versus objects: a code has a secret and
 * nobody holds it, an object has a holder and usually no secret. 'other' is
 * there so somebody with a badge reader or a smart lock is not forced to
 * mislabel it as a fob.
 */
export const SITE_ACCESS_KINDS = ['alarm_code', 'door_code', 'gate_code', 'key', 'fob', 'lockbox', 'other'] as const;
export type SiteAccessKind = (typeof SITE_ACCESS_KINDS)[number];

/**
 * What a client raised (0093).
 *
 * A complaint and a request are genuinely different — one is a failure and
 * one is an ask — and a log that conflated them would let a company report a
 * healthy response rate on nothing but requests for extra bins.
 */
export const SERVICE_REQUEST_KINDS = ['complaint', 'request', 'question'] as const;
export type ServiceRequestKind = (typeof SERVICE_REQUEST_KINDS)[number];

export const SERVICE_REQUEST_STATUSES = ['open', 'acknowledged', 'resolved', 'closed'] as const;
export type ServiceRequestStatus = (typeof SERVICE_REQUEST_STATUSES)[number];

/**
 * The life of a quality inspection (0092).
 *
 * 'in_progress' exists because a supervisor walks a building over twenty
 * minutes on a phone with patchy signal, and a form that only had "not
 * started" and "done" would lose that walkthrough the first time a lift went
 * between floors.
 */
export const INSPECTION_STATUSES = ['scheduled', 'in_progress', 'completed', 'cancelled'] as const;
export type InspectionStatus = (typeof INSPECTION_STATUSES)[number];

/**
 * The scoring scale, 1–5, which is what every janitorial inspection form in
 * the trade already uses. Exported so the app and the API agree on the bounds
 * rather than each hard-coding them.
 */
export const INSPECTION_RATING_MIN = 1;
export const INSPECTION_RATING_MAX = 5;

/** How long a line's area and check may be, and how many lines one walkthrough holds. */
export const INSPECTION_AREA_NAME_MAX = 120;
export const INSPECTION_ITEM_NAME_MAX = 160;
export const INSPECTION_LINES_MAX = 300;

/**
 * Where a restock request is up to (0090).
 *
 * A flag with no state answers "what do I need to buy this week" exactly
 * once; these four let the office work the list and still see what they are
 * waiting on.
 */
export const RESTOCK_STATUSES = ['open', 'ordered', 'delivered', 'cancelled'] as const;
export type RestockStatus = (typeof RESTOCK_STATUSES)[number];

/**
 * The paperwork a commercial client asks for before a crew is let in (0091).
 *
 * 'coi' is the certificate of insurance and is the one that matters most: it
 * expires annually, most facilities managers want their own name on it, and
 * a lapsed one is discovered by a crew at a locked door.
 */
export const COMPLIANCE_DOCUMENT_KINDS = [
  'coi',
  'w9',
  'license',
  'background_check',
  'badging',
  'bond',
  'other',
] as const;
export type ComplianceDocumentKind = (typeof COMPLIANCE_DOCUMENT_KINDS)[number];

/**
 * How many days ahead of expiry the office is warned about a document.
 *
 * Two warnings, not one. Thirty days is enough to renew a certificate of
 * insurance through a broker; seven is the one that gets acted on. A single
 * reminder is either too early to act on or too late to fix.
 */
export const COMPLIANCE_EXPIRY_WARNING_DAYS = [30, 7] as const;

/**
 * The life of a change order.
 *
 * 'draft' while the office writes it, 'sent' once the client has been asked,
 * then the client's answer or the office's withdrawal. The last three are
 * terminal: an approved order is work the client agreed to buy, and unsaying
 * that is a credit note, not an edit.
 */
export const CHANGE_ORDER_STATUSES = ['draft', 'sent', 'approved', 'declined', 'cancelled'] as const;
export type ChangeOrderStatus = (typeof CHANGE_ORDER_STATUSES)[number];

/**
 * Why approved extra work could not go onto its visit (migration 0104): the
 * visit-price reasons (`VisitPricing['blockedReason']`), plus a visit that is
 * gone, one already carrying the most price lines a visit takes, and a total
 * the extra would push out of range.
 */
export const EXTRA_WORK_VISIT_BLOCKED_REASONS = [
  'unpriced',
  'closed',
  'money_started',
  'currency_changed',
  'price_changed',
  'range_quote',
  'single_job',
  'visit_missing',
  'too_many_lines',
  'invalid_total',
] as const;
export type ExtraWorkVisitBlockedReason = (typeof EXTRA_WORK_VISIT_BLOCKED_REASONS)[number];

/**
 * How far from the property a clock-in can be and still count as on site.
 * Generous on purpose: a commercial address geocodes to the middle of a
 * building that can be a hundred meters deep, GPS drifts indoors, and crews
 * park where they can. The distance itself is always stored and shown — this
 * threshold only decides which badge the office sees.
 */
export const ON_SITE_DISTANCE_METERS = 300;

export const DISCOUNT_TYPES = ['fixed', 'percentage'] as const;
export type DiscountType = (typeof DISCOUNT_TYPES)[number];

/**
 * Margin-gated discounts (migration 0118). What a discount that would push a
 * quote line below the company's margin floor does:
 *
 *   * 'override' — the default: refused unless an owner or manager approves it
 *     with a reason, and every approval is kept in the override trail.
 *   * 'block' — refused outright; the discount has to come down.
 *   * 'off' — margins are still shown to staff, nothing is refused.
 */
export const MARGIN_FLOOR_MODES = ['off', 'block', 'override'] as const;
export type MarginFloorMode = (typeof MARGIN_FLOOR_MODES)[number];
/** 15%: the floor a company starts with until the owner changes it. */
export const DEFAULT_MARGIN_FLOOR_BASIS_POINTS = 1500;
export const DEFAULT_MARGIN_FLOOR_MODE: MarginFloorMode = 'override';
export const MARGIN_OVERRIDE_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type MarginOverrideStatus = (typeof MARGIN_OVERRIDE_STATUSES)[number];

// ---------------------------------------------------------------------------
// Billing intervals
// ---------------------------------------------------------------------------

export const BILLING_INTERVALS = ['month', 'year'] as const;
export type BillingInterval = (typeof BILLING_INTERVALS)[number];

export function isBillingInterval(value: unknown): value is BillingInterval {
  return typeof value === 'string' && (BILLING_INTERVALS as readonly string[]).includes(value);
}

/**
 * Yearly prices are exactly ten monthly payments — "two months free". Applied
 * uniformly to every price component (plan base, extra seat, extra location),
 * so the Stripe yearly price catalog is this multiple of the monthly one.
 */
export const YEARLY_PRICE_MULTIPLIER = 10;

// ---------------------------------------------------------------------------
// Sessions (spec §6.3)
// ---------------------------------------------------------------------------

export const SESSION_INACTIVITY_DAYS = 7;
export const SESSION_ABSOLUTE_DAYS = 30;
export const SESSION_ABSOLUTE_DAYS_REMEMBER_ME = 90;
export const PASSWORD_RESET_TOKEN_MINUTES = 60;
export const CSRF_COOKIE_NAME = 'cqb_csrf';
export const CSRF_HEADER_NAME = 'x-csrf-token';
/**
 * The location a browser tab is showing, sent on every write so the Worker can
 * refuse one that would land in a location the session has since switched to
 * in another tab (middleware/expected-location.ts). Optional: a request without
 * it (the mobile app, API keys, an older tab) behaves exactly as before.
 */
export const EXPECTED_LOCATION_HEADER_NAME = 'x-expected-location-id';

// ---------------------------------------------------------------------------
// Social sign-in
// ---------------------------------------------------------------------------

/**
 * In display order, which is also the order the buttons appear in. Apple sits
 * second because Apple's own guidance is that "Sign in with Apple" should not
 * be shown less prominently than the alternatives beside it.
 *
 * LinkedIn sits with the four directories rather than the three platforms below
 * it, because it does report an email address and can therefore start an
 * account. It is fifth rather than higher only because it is the newest.
 *
 * The last three are the social platforms rather than the account directories,
 * and they differ in one way that matters: none of them will tell us an email
 * address, ever. That does not stop them signing anybody in — an identity is
 * matched on the provider's subject id, never on an address — but it does mean
 * they cannot *start* an account here on their own, because a tenant has to be
 * created against an address. So they are connected from Settings by someone
 * already signed in, and sign in on their own from then on. See
 * `providerSharesEmail` in services/oauth/providers.ts, which is what the
 * sign-in route reads to say so in words a visitor can act on.
 *
 * `x` is the id for X, formerly Twitter: the id, the label, the callback path
 * and the credential names all say the same thing, which is the invariant every
 * other provider here already keeps.
 */
export const OAUTH_PROVIDERS = [
  'google',
  'apple',
  'facebook',
  'microsoft',
  'linkedin',
  'x',
  'instagram',
  'tiktok',
] as const;
export type OAuthProvider = (typeof OAUTH_PROVIDERS)[number];

export const OAUTH_PROVIDER_LABELS: Readonly<Record<OAuthProvider, string>> = {
  google: 'Google',
  apple: 'Apple',
  facebook: 'Facebook',
  microsoft: 'Microsoft',
  linkedin: 'LinkedIn',
  x: 'X',
  instagram: 'Instagram',
  tiktok: 'TikTok',
};

export function isOAuthProvider(value: string): value is OAuthProvider {
  return (OAUTH_PROVIDERS as readonly string[]).includes(value);
}

/**
 * The providers that will never report an email address.
 *
 * Not a matter of scopes or of what the visitor agreed to: X's v2 user object,
 * Instagram Login and TikTok's Login Kit publish no email field at all, so
 * there is nothing to ask for. Facebook is deliberately NOT here — it has an
 * address and may simply not have been given permission to share it, which is
 * a different situation and one the visitor can go and fix.
 *
 * This list is the single source of that fact, because three places have to
 * agree on it and drift between them is silent:
 *
 *   * the sign-in route, choosing which of two "no address" messages to show;
 *   * the button row, which must not offer these three as a way to *sign up*;
 *   * anyone reading either of those and wondering what makes the three alike.
 *
 * It lives here rather than in the Worker's provider table because the browser
 * needs it too — see SocialSignInButtons.
 */
export const OAUTH_PROVIDERS_WITHOUT_EMAIL: readonly OAuthProvider[] = ['x', 'instagram', 'tiktok'];

/** Whether this provider is capable of reporting an email address at all. */
export function oauthProviderSharesEmail(provider: OAuthProvider): boolean {
  return !OAUTH_PROVIDERS_WITHOUT_EMAIL.includes(provider);
}

/**
 * The providers that can *create* an account, in display order.
 *
 * An account is created against an email address, so a provider with none to
 * give cannot start one — it can only join an account that already exists, by
 * being connected from Settings. Offering one on a sign-up surface would be a
 * button that takes somebody out to a provider, brings them back, and refuses
 * them. The four that remain are the four that work there.
 */
export const OAUTH_SIGNUP_PROVIDERS: readonly OAuthProvider[] = OAUTH_PROVIDERS.filter(oauthProviderSharesEmail);

/**
 * Sentinel stored in `users.password_algorithm` for an account that has no
 * password — someone who signed up with a provider and has never set one. The
 * password columns are NOT NULL (see migration 0008), and
 * `verifyPassword` refuses this algorithm outright, so the sentinel cannot
 * verify against any input.
 */
export const NO_PASSWORD_ALGORITHM = 'none';

/** Carries state, nonce and PKCE verifier across the provider round trip. */
export const OAUTH_STATE_COOKIE_NAME = 'cqb_oauth';
/**
 * Carries a provider-verified identity that has no account yet, while the
 * visitor types the company name that a new tenant cannot be created without.
 */
export const OAUTH_SIGNUP_COOKIE_NAME = 'cqb_oauth_signup';
/**
 * Carries a provider-verified identity across one same-site hop, for a provider
 * that returns with a cross-site POST (Apple). The session cookie is
 * `SameSite=Lax` and so is absent on that POST — see routes/oauth.ts.
 */
export const OAUTH_LINK_COOKIE_NAME = 'cqb_oauth_link';
/** A redirect to a provider and back is seconds of work; minutes is generous. */
export const OAUTH_STATE_TTL_MINUTES = 15;
/** One automatic hop, taken immediately. Minutes only for a slow browser. */
export const OAUTH_LINK_TTL_MINUTES = 5;
/** Long enough to read the page and pick a company name, short enough to expire. */
export const OAUTH_SIGNUP_TTL_MINUTES = 30;

/**
 * Where a social sign-in started by the mobile app comes back to.
 *
 * The app runs the provider leg in the platform's sign-in browser
 * (ASWebAuthenticationSession, Custom Tabs), whose cookies are not the app's,
 * so the flow cannot end by setting a session cookie the way the website's
 * does. It ends by navigating here instead — a URL the sign-in browser hands
 * back to the app rather than loading — carrying either a failure code or a
 * one-time handoff code the app redeems for its own session. See
 * services/oauth/native-handoff.ts.
 */
export const NATIVE_OAUTH_CALLBACK_URL = 'blastoclean://oauth';
/**
 * Where Stripe Connect sends the mobile app after the HTTPS callback has
 * stored or refused the link.
 *
 * Stripe's redirect_uri stays the HTTPS callback (it is registered with
 * Stripe). The app opens the authorize URL in the same sign-in browser as
 * social sign-in, and this fixed address is what that browser hands back.
 * `nativeReturn: true` on connect/start selects it. The client never supplies
 * the URL, so the flag cannot become an open redirect. Query: `stripeConnect`
 * (linked, declined, error, expired, in-use, unavailable) and, when the flow
 * started on a quote, `returnPath` (one quote page only).
 */
export const NATIVE_STRIPE_CONNECT_RETURN_URL = 'blastoclean://stripe-connect';
/**
 * Where a Twilio Connect started by the mobile app comes back to: the same
 * scheme as NATIVE_OAUTH_CALLBACK_URL, so the platform's sign-in browser hands
 * it to the app instead of loading it. It carries the outcome
 * (`twilioConnect=`), or the state and Account SID the app finishes the
 * connection with (POST /api/v1/company/sms-settings/connect/finish).
 */
export const TWILIO_CONNECT_NATIVE_CALLBACK_URL = 'blastoclean://twilio-connect';
/**
 * Marks a sign-in browser whose latest social sign-in the app started, so a
 * callback whose flow cookie has expired still sends its failure back to the
 * app instead of to a page the app's sign-in sheet would show. Says nothing
 * else and grants nothing.
 */
export const OAUTH_NATIVE_COOKIE_NAME = 'cqb_oauth_native';
/**
 * A sign-in handoff: the app redeems it the moment the sign-in browser closes.
 * Minutes only for a slow network. A signup handoff lives as long as
 * OAUTH_SIGNUP_TTL_MINUTES instead.
 */
export const NATIVE_OAUTH_HANDOFF_TTL_MINUTES = 3;

// ---------------------------------------------------------------------------
// The operator CMS (/admin)
// ---------------------------------------------------------------------------

/**
 * The operator's session, deliberately a different cookie from the tenant
 * one: an operator signing into /admin must not sign a tenant tab out, and a
 * tenant session must never be mistakable for an operator's. Scoped to
 * /api/admin so it is not even sent anywhere else.
 */
export const ADMIN_SESSION_COOKIE_NAME = 'cqb_admin_session';
/**
 * The operator CSRF token, readable by the /admin pages (double-submit).
 * A separate name so admin sign-in cannot clobber `cqb_csrf` in a
 * concurrently open tenant tab.
 */
export const ADMIN_CSRF_COOKIE_NAME = 'cqb_admin_csrf';
/** Carries state, nonce and PKCE verifier across the admin Google round trip. */
export const ADMIN_OAUTH_STATE_COOKIE_NAME = 'cqb_admin_oauth';

/** Operators sign in with a click, so the shorter windows cost them little. */
export const ADMIN_SESSION_INACTIVITY_DAYS = 7;
export const ADMIN_SESSION_ABSOLUTE_DAYS = 30;

/**
 * The partner portal's own cookies.
 *
 * A third pair rather than a reuse of either existing one, for the same reason
 * the operator console has its own: a partner signing in must not sign out a
 * customer's tab on the same browser, and no cookie should be satisfiable by
 * another surface's. Scoped to /api/partner, so an ordinary request never
 * carries them.
 */
export const PARTNER_SESSION_COOKIE_NAME = 'cqb_partner_session';
export const PARTNER_CSRF_COOKIE_NAME = 'cqb_partner_csrf';

/**
 * Longer than an operator's window and shorter than a customer's: a partner
 * checks their commission a few times a month, so being signed out weekly is
 * an irritation, while the account can authorize a change to where money is
 * sent.
 */
export const PARTNER_SESSION_INACTIVITY_DAYS = 14;
export const PARTNER_SESSION_ABSOLUTE_DAYS = 60;

/**
 * How long a session minted by "sign in as this user" lasts — both windows,
 * inactivity and absolute.
 *
 * Deliberately an hour rather than the tenant default of seven and thirty
 * days: this is a support tool for looking at one problem, and a borrowed
 * identity is the one session that must not sit in a browser profile for a
 * month. Re-entering costs the operator two clicks.
 */
export const IMPERSONATION_SESSION_MINUTES = 60;

// ---------------------------------------------------------------------------
// Uploads (spec §14.3)
// ---------------------------------------------------------------------------

export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
export const MAX_ATTACHMENTS_PER_QUOTE = 25;
export const MAX_ATTACHMENT_BYTES_PER_QUOTE = 100 * 1024 * 1024;

export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const ALLOWED_ATTACHMENT_TYPES = ['application/pdf', ...ALLOWED_IMAGE_TYPES] as const;

/** Magic-number prefixes used to verify a declared content type (spec §14.3). */
export const FILE_SIGNATURES: Readonly<Record<string, readonly number[][]>> = {
  'image/jpeg': [[0xff, 0xd8, 0xff]],
  'image/png': [[0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]],
  // RIFF....WEBP — bytes 8..11 are checked separately.
  'image/webp': [[0x52, 0x49, 0x46, 0x46]],
  'application/pdf': [[0x25, 0x50, 0x44, 0x46]],
};

export const CONTENT_TYPE_EXTENSIONS: Readonly<Record<string, string>> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'application/pdf': 'pdf',
};

// ---------------------------------------------------------------------------
// Scheduling
// ---------------------------------------------------------------------------

export const SERIES_FREQUENCIES = ['one_time', 'weekly', 'biweekly', 'monthly'] as const;
export type SeriesFrequency = (typeof SERIES_FREQUENCIES)[number];

export const SERIES_FREQUENCY_LABELS: Readonly<Record<SeriesFrequency, string>> = {
  one_time: 'One time',
  weekly: 'Weekly',
  biweekly: 'Every two weeks',
  monthly: 'Monthly',
};

export const SERIES_STATUSES = ['active', 'paused', 'ended'] as const;
export type SeriesStatus = (typeof SERIES_STATUSES)[number];

export const SERIES_STATUS_LABELS: Readonly<Record<SeriesStatus, string>> = {
  active: 'Active',
  paused: 'Paused',
  ended: 'Ended',
};

export const APPOINTMENT_STATUSES = [
  'scheduled',
  'confirmed',
  'change_requested',
  'completed',
  'cancelled',
  'no_show',
] as const;
export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number];

export const APPOINTMENT_STATUS_LABELS: Readonly<Record<AppointmentStatus, string>> = {
  scheduled: 'Scheduled',
  confirmed: 'Confirmed',
  change_requested: 'Change requested',
  completed: 'Completed',
  cancelled: 'Canceled',
  no_show: 'No show',
};

/** Statuses that still occupy their slot — what the calendar and feed count as live. */
export const OPEN_APPOINTMENT_STATUSES: readonly AppointmentStatus[] = ['scheduled', 'confirmed', 'change_requested'];

export const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
] as const;

export const NTH_WEEK_LABELS: Readonly<Record<number, string>> = {
  1: 'First',
  2: 'Second',
  3: 'Third',
  4: 'Fourth',
  5: 'Last',
};

/** How far ahead the cron keeps occurrences materialized. */
export const SCHEDULE_HORIZON_DAYS = 84;

/**
 * How long before a visit its invoice is written. Visits sit on the calendar
 * months ahead; an invoice for each one from the day it was scheduled filled
 * the invoice list with bills for next spring. The customer still hears about
 * it only once the visit starts (`dispatchInvoiceNotices`).
 */
export const VISIT_INVOICE_LEAD_DAYS = 7;

export const DEFAULT_CONFIRM_LEAD_HOURS = 48;
export const DEFAULT_REMINDER_LEAD_HOURS = 24;

// ---------------------------------------------------------------------------
// SMS reminders
//
// Texts go out on one Twilio account shared by every company on the
// deployment, so the limits below are not tuning knobs — they are what stops
// one tenant's bad data spending everyone else's budget and getting the shared
// sender flagged by the carriers. Each is enforced independently in
// services/sms.ts; a send has to clear all of them.
// ---------------------------------------------------------------------------

export const SMS_CONSENT_STATUSES = ['unknown', 'opted_in', 'opted_out'] as const;
export type SmsConsentStatus = (typeof SMS_CONSENT_STATUSES)[number];

export const SMS_CONSENT_SOURCES = ['office', 'customer_page', 'sms_reply'] as const;
export type SmsConsentSource = (typeof SMS_CONSENT_SOURCES)[number];

/**
 * What `customers.sms_consent_source` may store.
 *
 * `web_form` is a quote-form tick. It is not a value of the public
 * `smsConsentSource` field: that enum is part of the mobile customer
 * response, and widening it would break installed apps. The API reports
 * the tick on the optional `smsConsentQuoteForm` field instead.
 */
export const SMS_CONSENT_RECORD_SOURCES = [...SMS_CONSENT_SOURCES, 'web_form'] as const;
export type SmsConsentRecordSource = (typeof SMS_CONSENT_RECORD_SOURCES)[number];

export const SMS_CONSENT_STATUS_LABELS: Readonly<Record<SmsConsentStatus, string>> = {
  unknown: 'Not asked',
  opted_in: 'Opted in',
  opted_out: 'Opted out',
};

export const SMS_CONSENT_SOURCE_LABELS: Readonly<Record<SmsConsentSource, string>> = {
  office: 'Recorded by the office',
  customer_page: 'Chosen by the customer online',
  sms_reply: 'By text reply',
};

/**
 * Per number, per 24-hour window. A visit generates at most two texts, so
 * anything beyond a handful means a customer is on several schedules, an
 * owner also holds the office phone that change-request and reminder alerts
 * both reach, or something is wrong. Fifteen is enough headroom for a busy
 * shared number to hear about several visits in a day, while still being
 * far short of the hundreds a runaway loop would produce.
 *
 * The window is fixed rather than sliding (see services/sms.ts), so the real
 * worst case is twice this across a reset boundary. That is sized in: thirty
 * texts in a bad hour is still a bounded, recoverable annoyance, where the
 * failure this exists to stop is hundreds.
 */
export const SMS_PER_RECIPIENT_DAILY_LIMIT = 15;

/** Default for a company's own ceiling; owners can lower it in Settings. */
export const SMS_COMPANY_DAILY_LIMIT_DEFAULT = 250;
export const SMS_COMPANY_DAILY_LIMIT_MAX = 5000;

/**
 * Deployment-wide, per dispatch cycle.
 *
 * The window matches the cron's 15 minutes on purpose. A per-*minute* bucket
 * looks stricter but is far stricter than intended: the cron is the only thing
 * that sends, it runs for seconds, and everything it hands over lands inside a
 * single minute — so a 30-per-minute cap really means 30 per quarter hour, or
 * two a minute averaged out. A deployment with a few hundred visits in the same
 * evening would then dribble reminders out over hours and some would arrive
 * after the visit.
 *
 * 450 per cycle is the same 30-a-minute sustained rate, expressed in the unit
 * the dispatcher actually works in. Pacing *within* a burst is Twilio's job —
 * it queues per-number throughput on its side — so the cap here is a runaway
 * brake, not a throughput shaper.
 */
export const SMS_GLOBAL_BURST_LIMIT = 450;
export const SMS_GLOBAL_BURST_WINDOW_SECONDS = 15 * 60;
export const SMS_GLOBAL_DAILY_LIMIT = 5000;

/**
 * Local hours (in the *visit's* timezone) outside which nothing is sent.
 * Reminders are deferred to the next open hour rather than dropped, so a
 * 3am-due reminder for a 9am visit still arrives — at 8am. Quiet hours are law
 * in most US states, not a courtesy.
 */
export const SMS_QUIET_HOURS_START = 21;
export const SMS_QUIET_HOURS_END = 8;

/** Longest sender label that leaves room for the message itself. */
export const SMS_SENDER_LABEL_MAX = 24;

/**
 * Used when a company name has no GSM-7 form at all — a name written in a
 * non-Latin script. Generic on purpose: guessing at a transliteration would be
 * worse than saying plainly what the message is about.
 */
export const SMS_SENDER_LABEL_FALLBACK = 'Cleaning visit';

/**
 * How long a cached line-type answer is trusted before it is asked again.
 *
 * Numbers do get ported between landline and mobile, so the answer is not
 * permanent — but it changes rarely enough that paying Twilio for it on every
 * send would be spending real money to learn the same thing. Ninety days is
 * roughly "once a quarter per number anyone is still texting", and a number
 * nobody texts is never re-checked at all.
 */
export const PHONE_LINE_TYPE_RECHECK_DAYS = 90;

/** What Twilio's Lookup API can tell us about a line. */
export const PHONE_LINE_TYPES = ['mobile', 'landline', 'voip', 'unknown'] as const;
export type PhoneLineTypeCode = (typeof PHONE_LINE_TYPES)[number];

/** Where a single SMS stops and multi-part billing begins (GSM-7). */
export const SMS_SEGMENT_LENGTH = 160;

/**
 * The ceiling on a whole outbound message, in billed segments.
 *
 * Reminders are fixed wording and land inside one segment on their own. A
 * texted quote is the first message with a part somebody types — the note that
 * travels with it — and a box with no ceiling is a box someone will paste four
 * paragraphs into, at a segment per 153 characters, per recipient, on an
 * account shared with every other tenant.
 *
 * Three is the ceiling rather than one because the message has to carry a link
 * that cannot be shortened or cut, and a bare "your quote is ready" with the
 * link already runs close to a single segment — so a one-segment ceiling would
 * mean no note at all. Three leaves roughly two segments of room for the note
 * and still bounds the cost of any one send at three times a reminder.
 */
export const SMS_QUOTE_MAX_SEGMENTS = 3;

/**
 * The longest note the API will accept alongside a texted quote.
 *
 * The segment ceiling above is what actually decides how much of a note goes
 * out — it accounts for the link and the label, which this number cannot — so
 * this is the outer bound the form and the schema agree on, not the real limit.
 * It is deliberately a little larger than two segments of GSM-7 (306
 * characters): a note that is trimmed by a few words is a better answer than a
 * form that refuses to accept what was typed, and the trim is shown back to
 * the sender before it goes.
 */
export const SMS_QUOTE_NOTE_MAX = 320;

/**
 * The ceiling on a reschedule-request alert to staff.
 *
 * Two rather than three, and for the opposite reason to the quote above: there
 * is no link to protect, and the part that would overflow — the customer's own
 * words — is already in the email and in the app. What this message has to do
 * is reach a phone in a van with the who, the when, and the fact that it is a
 * reschedule; the rest is a courtesy worth about one segment, not three. The
 * cost is also multiplied here in a way the quote's is not: one request can
 * text every owner and the assigned cleaner.
 */
export const SMS_CHANGE_REQUEST_MAX_SEGMENTS = 2;

/**
 * How long a canceled-visit text to the office may be.
 *
 * Two segments, same budget as a change-request text. The link has to survive
 * whole, so a long customer name is what gets shortened.
 */
export const SMS_VISIT_CANCELLED_MAX_SEGMENTS = 2;

/**
 * The ceiling on a text somebody typed themselves, from the customer's
 * conversation.
 *
 * Higher than `SMS_QUOTE_MAX_SEGMENTS` because none of this message is
 * scaffolding: a quote text is a fixed sentence plus a link, with the note as
 * the only part worth room, while here the message *is* the whole of what the
 * sender wanted to say. Four segments is roughly 600 characters of GSM-7 —
 * long enough for a real explanation, short enough that no single send costs
 * more than four reminders.
 *
 * Unlike the note ceilings above, this one never TRIMS. A note that loses its
 * last few words is still a quote text; a person's own message with the end
 * cut off is a different message from the one they pressed send on, so an
 * over-long text is refused and the sender is told what it would cost.
 */
export const SMS_CUSTOMER_MESSAGE_MAX_SEGMENTS = 4;

/**
 * The longest ad-hoc text the API will accept, before the sender label goes in
 * front of it.
 *
 * The segment ceiling above is the real limit — it counts the label, and it
 * knows that one emoji drops the whole message from 160 characters a segment
 * to 70. This is the outer bound the composer and the schema agree on, so a
 * paste of four paragraphs is refused by the form rather than measured.
 */
export const SMS_CUSTOMER_MESSAGE_MAX = 800;

/** The subject line on a message somebody typed from the customer's timeline. */
export const CUSTOMER_MESSAGE_SUBJECT_MAX = 200;

/**
 * The body of an emailed message somebody typed themselves.
 *
 * Email is not billed by the segment, so this is a sanity bound rather than a
 * cost one — the same order as `internalNotes` on the customer record.
 */
export const CUSTOMER_MESSAGE_BODY_MAX = 10_000;

/**
 * An internal note in the customer's timeline.
 *
 * Much shorter than a message, because it is a margin note rather than a
 * document — "prefers texts after 4pm", "gate code changed" — and because
 * anything longer belongs on the customer record where it can be found without
 * scrolling a thread. Shared with `POST /messages/notes`, which enforces it, so
 * the box cannot accept what the server will refuse.
 */
export const INTERNAL_NOTE_MAX = 2_000;

/**
 * How much of a customer's emailed reply is kept.
 *
 * Generous, because unlike a text this costs nothing to carry and a customer
 * explaining a problem properly is exactly the message worth having in full.
 * Bounded anyway, because an inbound handler with no ceiling is a way to fill
 * a database from outside.
 *
 * This applies to the reply AFTER the quoted original has been stripped. A
 * thread quoted in full on every exchange is what makes an email timeline
 * unreadable by the third message.
 */
export const INBOUND_EMAIL_BODY_MAX = 20_000;
/**
 * The longest inbound subject kept.
 *
 * A header is foldable without limit, so this is the difference between a
 * column beside a bounded body and one an outsider can fill.
 */
export const INBOUND_EMAIL_SUBJECT_MAX = 500;
/** The longest `Message-ID` treated as one. RFC 5322 suggests far less. */
export const INBOUND_EMAIL_MESSAGE_ID_MAX = 400;

/**
 * The largest file a reply can carry into R2, and how many of them.
 *
 * A customer photographing a stained carpet is the case this exists for, so
 * the ceiling is a couple of phone photos rather than a document archive.
 * Anything larger is still ACKNOWLEDGED — the attachment is recorded with its
 * name and size and no bytes — because a timeline that silently drops a file
 * the customer watched themselves attach is worse than one that says "too
 * large to keep".
 */
export const INBOUND_EMAIL_ATTACHMENT_MAX_BYTES = 8 * 1024 * 1024;
export const INBOUND_EMAIL_ATTACHMENT_MAX_COUNT = 10;

// ---------------------------------------------------------------------------
// Payments
// ---------------------------------------------------------------------------

export const BILLING_MODES = ['manual', 'automatic'] as const;
export type BillingMode = (typeof BILLING_MODES)[number];

export const BILLING_MODE_LABELS: Readonly<Record<BillingMode, string>> = {
  manual: 'Manual — the office charges each visit',
  automatic: 'Automatic — charge the card on file when a visit is completed',
};

/** Per-appointment rollup of its ledger. */
export const APPOINTMENT_PAYMENT_STATUSES = [
  'not_billed',
  'pending',
  'paid',
  'failed',
  'refunded',
  'partially_refunded',
] as const;
export type AppointmentPaymentStatus = (typeof APPOINTMENT_PAYMENT_STATUSES)[number];

export const APPOINTMENT_PAYMENT_STATUS_LABELS: Readonly<Record<AppointmentPaymentStatus, string>> = {
  not_billed: 'Not billed',
  pending: 'Payment pending',
  paid: 'Paid',
  failed: 'Payment failed',
  refunded: 'Refunded',
  partially_refunded: 'Partially refunded',
};

/**
 * How much of an accepted one-time quote the customer has paid, from the
 * payments recorded on the quote itself (0036_quote_payments.sql). Derived
 * on read by `quotePaymentStatus`, never stored. A recurring or monthly quote
 * has none: its money is per visit, so a figure against the quote's own total
 * would mean nothing.
 */
export const QUOTE_PAYMENT_STATUSES = ['unpaid', 'partly_paid', 'paid'] as const;
export type QuotePaymentStatus = (typeof QUOTE_PAYMENT_STATUSES)[number];

/**
 * An invoice's status mirrors its visit's `AppointmentPaymentStatus` — see
 * migration 0032 — plus `void`, for a canceled visit's invoice.
 */
export const INVOICE_STATUSES = [...APPOINTMENT_PAYMENT_STATUSES, 'void'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const INVOICE_STATUS_LABELS: Readonly<Record<InvoiceStatus, string>> = {
  ...APPOINTMENT_PAYMENT_STATUS_LABELS,
  void: 'Void',
};

export const PAYMENT_TYPES = ['charge', 'refund'] as const;
export type PaymentType = (typeof PAYMENT_TYPES)[number];

/**
 * How money arrived: through the provider (a stored card, a checkout link, a
 * card tapped in person) or recorded by the office itself (`manual` — cash, a
 * check, a bank transfer, anything on the company's payment-method list).
 */
export const PAYMENT_METHODS = ['card_on_file', 'checkout_link', 'manual', 'tap_to_pay'] as const;
export type PaymentMethodKind = (typeof PAYMENT_METHODS)[number];

/**
 * The families a company's own payment methods belong to. The method's *name*
 * is the company's label ("Zelle", "Gift card"); the family is what drives
 * icons and reporting, and never shows up in customer-facing copy.
 */
export const PAYMENT_METHOD_FAMILIES = ['cash', 'check', 'bank_transfer', 'card', 'other'] as const;
export type PaymentMethodFamily = (typeof PAYMENT_METHOD_FAMILIES)[number];

/**
 * What every company starts with. A deliberate small set: enough to record
 * how cleaning companies actually get paid on day one, and Settings is where
 * the rest ("Venmo", "Zelle", …) gets added.
 *
 * The family is the stable identity and the *name* is only its opening label,
 * so translating these is a matter of seeding the label the new owner can
 * read; renaming one afterwards works exactly as it always has.
 */
export const DEFAULT_PAYMENT_METHOD_FAMILIES = ['cash', 'check', 'bank_transfer', 'other'] as const;
export type DefaultPaymentMethodFamily = (typeof DEFAULT_PAYMENT_METHOD_FAMILIES)[number];

const DEFAULT_PAYMENT_METHOD_NAMES: Readonly<
  Record<SupportedLanguage, Readonly<Record<DefaultPaymentMethodFamily, string>>>
> = {
  en: { cash: 'Cash', check: 'Check', bank_transfer: 'Bank transfer', other: 'Other' },
  es: { cash: 'Efectivo', check: 'Cheque', bank_transfer: 'Transferencia bancaria', other: 'Otro' },
};

/**
 * The starter payment methods, labeled in `language` — and in the others.
 *
 * Same shape as the starter room and task library, and for the same reason:
 * this list is seeded from a translated source and then read from a picker by
 * whoever is recording the money, who reads the app in their own browser's
 * language rather than the company's. `name` is the company's own label from
 * the moment it lands; `nameTranslations` is what a reader in another language
 * sees instead. See `localized-text.ts`.
 */
export function defaultPaymentMethods(
  language: SupportedLanguage,
): ReadonlyArray<{ name: string; family: PaymentMethodFamily; nameTranslations: LocalizedText }> {
  const names = DEFAULT_PAYMENT_METHOD_NAMES[language];
  return DEFAULT_PAYMENT_METHOD_FAMILIES.map((family) => ({
    name: names[family],
    family,
    nameTranslations: Object.fromEntries(
      SUPPORTED_LANGUAGES.map((tag) => [tag, DEFAULT_PAYMENT_METHOD_NAMES[tag][family]]),
    ) as LocalizedText,
  }));
}

/**
 * Names Settings offers as one tap, beside the box for a name the company
 * types itself. These are brand names, so they stay the same words in every
 * language. A company that already has one — any case, surrounding spaces,
 * including a hidden row or a stored translation — does not see it again.
 */
export const SUGGESTED_PAYMENT_METHOD_NAMES = ['Zelle', 'Cash App', 'Venmo'] as const;

export function paymentMethodAlreadyNamed(
  methods: ReadonlyArray<{ name: string; nameTranslations?: LocalizedText | null }>,
  label: string,
): boolean {
  const wanted = label.trim().toLowerCase();
  if (wanted.length === 0) return false;
  return methods.some((method) =>
    [method.name, ...Object.values(method.nameTranslations ?? {})].some(
      (value) => typeof value === 'string' && value.trim().toLowerCase() === wanted,
    ),
  );
}

export const PAYMENT_STATUSES = ['pending', 'succeeded', 'failed', 'cancelled'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

// ---------------------------------------------------------------------------
// Quote requests (the public intake form)
// ---------------------------------------------------------------------------

export const QUOTE_REQUEST_STATUSES = ['new', 'converted', 'dismissed'] as const;
export type QuoteRequestStatus = (typeof QUOTE_REQUEST_STATUSES)[number];

/** Which public page a lead came through: the request form, or the photo-quote page (0041). */
export const QUOTE_REQUEST_SOURCES = ['form', 'photo_dump'] as const;
export type QuoteRequestSource = (typeof QUOTE_REQUEST_SOURCES)[number];

export const QUOTE_REQUEST_STATUS_LABELS: Readonly<Record<QuoteRequestStatus, string>> = {
  new: 'New',
  converted: 'Converted',
  dismissed: 'Dismissed',
};

/**
 * What the visitor may pick for "how often". Deliberately its own list rather
 * than SERIES_FREQUENCIES: a prospect asking for a quote has not committed to
 * a schedule, and "not sure yet" is the honest default answer.
 */
export const QUOTE_REQUEST_FREQUENCIES = ['one_time', 'weekly', 'biweekly', 'monthly', 'unsure'] as const;
export type QuoteRequestFrequency = (typeof QUOTE_REQUEST_FREQUENCIES)[number];

// ---------------------------------------------------------------------------
// Quote follow-ups
// ---------------------------------------------------------------------------

/** Choices offered for both follow-up settings; 0 renders as "Off". */
export const QUOTE_FOLLOW_UP_DAY_CHOICES = [0, 1, 2, 3, 5, 7, 10, 14] as const;
export const QUOTE_FOLLOW_UP_DAYS_MAX = 30;

// ---------------------------------------------------------------------------
// Delivery logs (GET /logs/* — logs:read, owner only)
// ---------------------------------------------------------------------------

/**
 * The outcomes an `email_log` row can hold, mirroring that table's CHECK
 * constraint (migration 0010). The logs page filters by these, and the API
 * validates the filter against this list rather than passing free text to SQL.
 */
export const EMAIL_LOG_STATUSES = ['queued', 'sent', 'delivered', 'failed', 'bounced', 'suppressed'] as const;
export type EmailLogStatus = (typeof EMAIL_LOG_STATUSES)[number];

/** The outcomes an `sms_log` row can hold (migration 0022's CHECK). */
export const SMS_LOG_STATUSES = ['queued', 'sent', 'delivered', 'undelivered', 'failed'] as const;
export type SmsLogStatus = (typeof SMS_LOG_STATUSES)[number];

// ---------------------------------------------------------------------------
// Visit photos
// ---------------------------------------------------------------------------

export const MAX_PHOTOS_PER_APPOINTMENT = 25;
export const MAX_PHOTO_BYTES_PER_APPOINTMENT = 100 * 1024 * 1024;

// ---------------------------------------------------------------------------
// Inspection photos
// ---------------------------------------------------------------------------

/**
 * Per walkthrough, not per line.
 *
 * Higher than a visit's allowance because the unit is different: a visit is
 * one clean with a few before-and-afters, while a walkthrough scores every
 * area in a building and the photograph is the argument for each low mark.
 * Forty areas photographed is a thorough inspector, not an abuse.
 */
export const MAX_PHOTOS_PER_INSPECTION = 60;
export const MAX_PHOTO_BYTES_PER_INSPECTION = 200 * 1024 * 1024;

// ---------------------------------------------------------------------------
// Address autocomplete
// ---------------------------------------------------------------------------

/**
 * Shortest address fragment worth a lookup.
 *
 * Below this every provider answers with either nothing or half a state, so
 * the request is pure cost. Shared because both sides enforce it: the browser
 * so it does not ask, and the Worker because the browser is the side that can
 * be skipped.
 */
export const ADDRESS_QUERY_MIN_LENGTH = 4;

/**
 * How long the address box waits for typing to stop before asking.
 *
 * Long enough that a house number and a street name are one lookup rather than
 * a dozen, short enough that the list is there by the time anyone looks for it.
 */
export const ADDRESS_QUERY_DEBOUNCE_MS = 350;

/**
 * How long it waits when the change was in one of the boxes *below* the street
 * — the city, state or postcode a lookup carries with it.
 *
 * Longer than the street's own wait, and deliberately: the street box is the
 * one somebody is watching a list appear under, so it stays quick, while the
 * locality is supporting detail nobody is watching for an answer. A trailing
 * debounce spends a lookup per keystroke on anybody typing slower than it
 * waits, and three boxes of it — a town, a state and a postcode — is where
 * that would add up against the hourly cap. Nearly a second means the whole of
 * "Springfield" is one lookup for all but the very slowest typist, and the
 * refresh still lands before anybody has finished moving back to the street.
 */
export const ADDRESS_CONTEXT_DEBOUNCE_MS = 900;

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 100;

// ---------------------------------------------------------------------------
// Public sharing (spec §15)
// ---------------------------------------------------------------------------

export const PUBLIC_TOKEN_BYTES = 32;
export const PUBLIC_TOKEN_DEFAULT_DAYS = 60;
